import { FC, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getErrorFromCatch } from "../../../api/api";
import { IPageParams } from "../../../api/IApi";
import { IPagesResponse } from "../../../api/IPagesResponse";
import { IOrder } from "../../../model/IOrder";
import { msg } from "../../../msg";
import Button from "../../form/Button";
import Table, { ITableTh } from "../../common/table/Table";
import LoadingOverlay from "../../common/loading/LoadingOverlay";
import OrdersRangeFilter from "../OrdersRangeFilter";
import { IDateRange } from "../orderDatePresets";

/**
 * One of the two order reports - unshipped, or late.
 *
 * They differ only in their columns, their query and whether the headers
 * sort, so the date range, the paging, the row count and the CSV download
 * live here once.
 */
const OrdersReportList: FC<{
  columns: ITableTh[],
  row: (order: IOrder) => React.ReactNode[],
  orders?: IPagesResponse<IOrder[]>,
  isFetching: boolean,
  isError: boolean,
  error?: unknown,
  range: IDateRange,
  onRangeChange: (range: IDateRange) => void,
  pageParams: IPageParams,
  onPageParamsChange: (params: Partial<IPageParams>) => void,
  storageKey: string,
  onDownload: () => Promise<void>,
  isDownloading?: boolean,
}> = ({
  columns,
  row,
  orders,
  isFetching,
  isError,
  error,
  range,
  onRangeChange,
  pageParams,
  onPageParamsChange,
  storageKey,
  onDownload,
  isDownloading = false,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    isError && error && toast.error(`${msg('load-orders-filed')} ${getErrorFromCatch(error)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  return (
    // -- The same white card, paddings and toolbar shape as Recent Orders and
    // -- the categorised views. `relative` is for the loading overlay, which
    // -- is absolutely positioned over this box.
    <div className="relative bg-white rounded-lg shadow-sm flex flex-col flex-1 min-h-0">
      <div className="flex justify-between gap-6 items-start px-4 sm:px-6 lg:px-8 pt-5">
        <OrdersRangeFilter range={range} onChange={onRangeChange} disabled={isFetching} />

        {/* Top right of the view: the count, and the whole thing as a file. */}
        <div className="flex items-center gap-4 pt-6 whitespace-nowrap">
          <span className="text-text_pale">{orders?.totalElements ?? 0} order(s)</span>
          {/* Fetched rather than linked: the session cookie is on another
              origin, and a plain link would skip the refresh-on-401. */}
          <Button
            isNotFullWidth
            size="small"
            variant="outline"
            disabled={isDownloading || !orders?.totalElements}
            onClick={onDownload}
          >
            {isDownloading ? 'Preparing…' : 'Download CSV'}
          </Button>
        </div>
      </div>

      {isFetching && orders?.content?.length ? <LoadingOverlay /> : null}

      {/*
        Table owns its own scrolling, in both axes, so the toolbar above never
        widens with it and the header stays pinned while the rows move.
      */}
      <div className="flex-1 min-h-0 flex flex-col">
        <Table
          fillHeight
          isPending={isFetching}
          thead={columns}
          pagable={orders}
          pageParams={pageParams}
          updatePageParams={(newParams) => onPageParamsChange(newParams)}
          isResizable
          resizeStorageKey={storageKey}
          pageSizeStorageKey={storageKey}
          tbody={(orders?.content || []).map((order) => ({
            onClick: () => navigate(`/orders/${order.orderId}`),
            data: row(order),
          }))}
        />
      </div>
    </div>
  );
};

export default OrdersReportList;
